import React, { useCallback } from 'react';
import { colors, zIndex } from '@/assets/styles';
import { Card } from '@/components/ui/cards/Card';
import { Text } from '@/components/ui/text/Text';
import { PrimaryButton } from '@/components/ui/buttons';
import { Divider } from '@/components/ui/separators';
import { useTranslation } from '@/i18n';
import type { RexSystem } from '@/api';


interface AppLauncherProps {
  rexSystems: RexSystem[];
  onClose: () => void;
}

interface AppLauncherItemProps {
  onClick: () => void;
  imageUrl: string;
  name: string;
}

const getSafeRedirectUrl = (authRedirectUri: string): string | null => {
  try {
    const normalizedUri = /^https?:\/\//i.test(authRedirectUri)
      ? authRedirectUri
      : `https://${authRedirectUri}`;

    const parsedUrl = new URL(normalizedUri);
    const isHttpsUrl = parsedUrl.protocol === 'https:';
    const isSameOriginHttpUrl =
      parsedUrl.protocol === 'http:' && parsedUrl.origin === window.location.origin;

    if (!isHttpsUrl && !isSameOriginHttpUrl) {
      return null;
    }

    return parsedUrl.toString();
  } catch {
    return null;
  }
};

const AppLauncherItem: React.FC<AppLauncherItemProps> = ({
  onClick,
  imageUrl,
  name,
}) => (
  <PrimaryButton
    onClick={onClick}
    variant="ghost"
    textAlign="left"
    className="py-2 px-2 rounded-md"
    iconLeft={
      <img
        src={imageUrl}
        alt={name}
        style={{ width: 37, height: 37 }}
        className="rounded object-cover"
      />
    }
    iconSize={37}
    customVariant={{
      default: {
        textColor: colors.primary.main,
      },
      pressed: {
        textColor: colors.primary.dark,
      },
      focus: {
        textColor: colors.primary.main,
      },
    }}
    textVariant="subheader-regular"
    fullWidth
  >
    {name}
  </PrimaryButton>
);

const AppLauncher: React.FC<AppLauncherProps> = ({ rexSystems, onClose }) => {
  const { t } = useTranslation();

  const handleSystemClick = useCallback((authRedirectUri: string) => {
    const safeRedirectUrl = getSafeRedirectUrl(authRedirectUri);
    if (!safeRedirectUrl) {
      return;
    }

    onClose();
    window.location.href = safeRedirectUrl;
  }, [onClose]);

  if (rexSystems.length === 0) {
    return null;
  }

  return (
    <Card
      padding="16px"
      className="tooltip-system-options absolute left-0 top-full mt-2 w-72"
      style={{ visibility: 'visible', zIndex: zIndex.menuDropdown }}
    >
      <div className="mb-3 px-2 text-left">
        <Text variant="body-medium" color={colors.primary.main}>
          {t('common:navigation.appLauncherTitle')}
        </Text>
      </div>

      <Divider color={colors.grays.neutralE5} />

      <div className="mt-2 flex flex-col gap-1">
        {rexSystems.map((system) => (
          <AppLauncherItem
            key={system.authRedirectUri}
            onClick={() => handleSystemClick(system.authRedirectUri)}
            imageUrl={system.smallLogo ?? ''}
            name={system.name}
          />
        ))}
      </div>
    </Card>
  );
};

export default AppLauncher;
